import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, BRAND } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}.`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthLayout>
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Welcome back</div>
      <h1 className="font-display text-4xl mb-8">Sign in to your protocol.</h1>

      <Button
        onClick={handleGoogle}
        data-testid="google-signin-btn"
        className="w-full rounded-full py-6 mb-4 bg-white text-black hover:bg-white/90 border-0"
      >
        <GoogleIcon /> Continue with Google
      </Button>

      <div className="flex items-center gap-3 text-[11px] text-white/30 my-6">
        <div className="flex-1 h-px bg-white/10" /> or email <div className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={handleEmailLogin} className="space-y-4">
        <div>
          <Label htmlFor="email" className="text-xs text-white/60">Email</Label>
          <div className="relative mt-1">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              className="bg-[#0F0F0F] border-white/10 text-white pl-9 py-5 rounded-lg focus:border-[#FF8A00]"
              placeholder="you@drthrive365.com"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="password" className="text-xs text-white/60">Password</Label>
          <div className="relative mt-1">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              className="bg-[#0F0F0F] border-white/10 text-white pl-9 py-5 rounded-lg focus:border-[#FF8A00]"
              placeholder="••••••••"
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full rounded-full py-6 bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 text-white"
        >
          {loading ? "Signing in…" : (<>Sign in <ArrowRight className="ml-2" size={16} /></>)}
        </Button>
      </form>

      <p className="text-sm text-white/50 mt-8">
        New here?{" "}
        <Link to="/register" className="text-[#FF8A00] hover:underline" data-testid="go-register">
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#050505] text-white">
      <div className="hidden md:flex flex-col justify-between p-10 bg-gradient-to-br from-[#FF8A00]/15 via-[#0A0A0A] to-[#E52E71]/10 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[400px] h-[400px] rounded-full bg-[#FF8A00]/20 blur-[120px]" />
        <Link to="/" className="flex items-center gap-3 relative z-10" data-testid="auth-brand">
          <img src={BRAND.logo} className="h-10 w-10 rounded-md" alt="" />
          <span className="font-display text-2xl">Dr Thrive 365</span>
        </Link>
        <div className="relative z-10">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-4">Thrive without limits</div>
          <p className="font-display text-5xl leading-tight max-w-sm">
            Rewire your biology.<br />
            <span className="text-brand-gradient">Step into purpose.</span>
          </p>
        </div>
        <p className="text-[10px] text-white/30 relative z-10 max-w-md">
          This content is for educational purposes only and not medical advice.
        </p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2 inline-block">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09A6.97 6.97 0 015.48 12c0-.73.13-1.44.35-2.09V7.07H2.18A10.98 10.98 0 001 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}
